import type {
  EntryConditions,
  HistoryPoint,
  HistoryRowRaw,
  OhlcResponse,
} from "../types";

/**
 * In dev: empty string → requests hit /api/... which the Vite proxy forwards.
 * In prod: set VITE_API_BASE_URL=https://martinapi.davianeng.com so we call directly.
 */
const RAW_BASE = (import.meta.env.VITE_API_BASE_URL ?? "").replace(/\/$/, "");
const API_BASE = RAW_BASE || "/api";

async function getJson<T>(path: string, signal?: AbortSignal): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, { signal });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`HTTP ${res.status} for ${path}: ${text.slice(0, 200)}`);
  }
  return (await res.json()) as T;
}

export function fetchEntryConditions(
  symbol: string,
  signal?: AbortSignal
): Promise<EntryConditions> {
  return getJson<EntryConditions>(
    `/entryconditionsv3/${encodeURIComponent(symbol)}`,
    signal
  );
}

export async function fetchEntryConditionsHistory(
  symbol: string,
  start: Date,
  end: Date,
  step: string = "5s",
  signal?: AbortSignal
): Promise<HistoryPoint[]> {
  const qs = new URLSearchParams({
    start: start.toISOString(),
    end: end.toISOString(),
    step,
  });
  const rows = await getJson<HistoryRowRaw[]>(
    `/entryconditionsv3/${encodeURIComponent(symbol)}/history?${qs}`,
    signal
  );
  return rows
    .map((row) => normalizeHistoryRow(row))
    .filter((p): p is HistoryPoint => p !== null);
}

export function fetchOhlc(
  symbol: string,
  start: Date,
  granularity: string,
  end?: Date,
  signal?: AbortSignal
): Promise<OhlcResponse> {
  const qs = new URLSearchParams({
    start: start.toISOString(),
    granularity,
  });
  if (end) qs.set("end", end.toISOString());
  return getJson<OhlcResponse>(
    `/ohlc/${encodeURIComponent(symbol)}?${qs}`,
    signal
  );
}

/** Positional mapping for entry_conditions_v10 columns. Must match the
 *  server-side table definition order. */
const EC_COLUMNS: (keyof EntryConditions)[] = [
  "cre_on",
  "symbol",
  "current_price",
  "atr_1m",
  "upper_bb_1m",
  "middle_bb_1m",
  "lower_bb_1m",
  "upper_bb_5m",
  "middle_bb_5m",
  "lower_bb_5m",
  "upper_bb_15m",
  "middle_bb_15m",
  "lower_bb_15m",
  "upper_bb_30m",
  "middle_bb_30m",
  "lower_bb_30m",
  "rsi_1m",
  "rsi_5m",
  "rsi_15m",
  "rsi_30m",
  "initial_trade",
  "recovery_trade",
];

const NUMERIC_KEYS: ReadonlySet<keyof EntryConditions> = new Set([
  "current_price",
  "atr_1m",
  "upper_bb_1m",
  "middle_bb_1m",
  "lower_bb_1m",
  "upper_bb_5m",
  "middle_bb_5m",
  "lower_bb_5m",
  "upper_bb_15m",
  "middle_bb_15m",
  "lower_bb_15m",
  "upper_bb_30m",
  "middle_bb_30m",
  "lower_bb_30m",
  "rsi_1m",
  "rsi_5m",
  "rsi_15m",
  "rsi_30m",
]);

/** Parse a Postgres composite literal like `(a,b,"c,d",,)` into an array of
 *  string|null values. Unquoted empty fields are null; `""` is the empty string. */
function parsePgComposite(input: string): (string | null)[] {
  if (!input.startsWith("(") || !input.endsWith(")")) return [];
  const body = input.slice(1, -1);
  const out: (string | null)[] = [];
  let cur = "";
  let inQuotes = false;
  let hasContent = false;
  for (let i = 0; i < body.length; i++) {
    const ch = body[i];
    if (inQuotes) {
      if (ch === '"') {
        if (body[i + 1] === '"') {
          cur += '"';
          i++;
        } else {
          inQuotes = false;
        }
        continue;
      }
      if (ch === "\\" && i + 1 < body.length) {
        cur += body[i + 1];
        i++;
        continue;
      }
      cur += ch;
      continue;
    }
    if (ch === '"') {
      inQuotes = true;
      hasContent = true;
      continue;
    }
    if (ch === ",") {
      out.push(hasContent ? cur : null);
      cur = "";
      hasContent = false;
      continue;
    }
    cur += ch;
    hasContent = true;
  }
  out.push(hasContent ? cur : null);
  return out;
}

function applyPositional(values: (string | null | unknown)[]): EntryConditions {
  const obj: Record<string, unknown> = {};
  EC_COLUMNS.forEach((key, i) => {
    const raw = values[i];
    if (raw == null) {
      obj[key] = null;
    } else if (NUMERIC_KEYS.has(key)) {
      const n = typeof raw === "number" ? raw : Number(raw);
      obj[key] = Number.isFinite(n) ? n : null;
    } else {
      obj[key] = typeof raw === "string" ? raw : String(raw);
    }
  });
  return obj as unknown as EntryConditions;
}

/** Turn a /history row into a flat HistoryPoint. The `snapshot` column comes
 *  from Postgres' `last(table.*, ts)` which is a composite type. Depending on
 *  the driver we may get a dict, an array, or a tuple-string. */
function normalizeHistoryRow(row: HistoryRowRaw): HistoryPoint | null {
  const snap = row.snapshot;
  if (snap && typeof snap === "object" && !Array.isArray(snap)) {
    return { bucket: row.bucket, ...(snap as unknown as EntryConditions) };
  }
  if (Array.isArray(snap)) {
    return { bucket: row.bucket, ...applyPositional(snap) };
  }
  if (typeof snap === "string") {
    const parts = parsePgComposite(snap);
    if (parts.length === 0) return null;
    return { bucket: row.bucket, ...applyPositional(parts) };
  }
  return null;
}
